/**
 * The search form's model, driven by the search schema (contract §2).
 *
 * Replaces the hand-maintained 47-field FormState and its toScrapeParams /
 * paramsToForm pair. The form is now `{ platform, mode, query, sounds,
 * params }`, where `params` is keyed by filter key (= ScrapeParams key), so a
 * new filter on the server needs no portal change.
 *
 * Everything here is pure, so it's unit tested without rendering.
 *
 * A few couplings the old form encoded by hand are kept as BUILT-IN rules,
 * applied when the server's schema doesn't say otherwise (the schema fields
 * `dependsOn`, `hiddenWhen` and `list` are client extensions):
 *   - audience countries / share / X verify only apply with the audience filter on
 *   - language share only applies once a language is picked
 *   - rotate sorts / time windows are replaced by Max reach
 *   - topic terms are sent as a list
 */
import { toPlatform, type Platform } from '../lib/platform';
import type {
  FieldErrors,
  ResolvedSound,
  ScrapeParams,
  SearchFilterDef,
  SearchModeDef,
  SearchParamValue,
  SearchSchema,
} from '../api/types';

export interface SearchFormState {
  platform: Platform;
  mode: string;
  /** The query box, as typed. Keyword modes: one search per line. */
  query: string;
  /** TikTok sound mode: resolved sounds (the box above takes pastes). */
  sounds: ResolvedSound[];
  /** Filter values keyed by filter key, in form units (see `toWire`). */
  params: Record<string, SearchParamValue>;
}

/** Params that are the query itself, rendered by the query box — never as filters. */
export const QUERY_PARAM_KEYS = ['keywords', 'keywordsList', 'hashtags', 'seedUsernames', 'musicId', 'musicIds', 'usernames'];
/** Params the form sets itself. */
const STRUCTURAL_KEYS = ['platform', 'searchMode', 'folderId', 'presetId', 'faceCreatorsOnly', 'region', 'alsoSearchGlobally'];

const BUILTIN_DEPENDS: Record<string, string> = {
  audienceCountries: 'audienceFilter',
  minAudienceShare: 'audienceFilter',
  xVerifyAudience: 'audienceFilter',
  minLanguageShare: 'contentLanguages',
};
const BUILTIN_HIDDEN_WHEN: Record<string, string> = {
  rotateSorts: 'maxReach',
  rotateTimeWindows: 'maxReach',
};
const BUILTIN_LIST = new Set(['topicTerms']);
/** Absent from a saved search = the default, not "off" (the old form's behaviour). */
const ABSENT_MEANS_DEFAULT = new Set(['targetCount', 'excludeOfficial']);

const KEYWORD_LIKE = new Set(['keyword', 'people', 'lists']);

export function isKeywordLike(mode: string): boolean {
  // Unknown modes from a newer server are treated as word searches.
  return KEYWORD_LIKE.has(mode) || !['hashtag', 'similar', 'sound', 'usernames'].includes(mode);
}

// ── Schema ────────────────────────────────────────────────────────────────

/**
 * Validate a schema response and fill in the built-in rules. Returns null for
 * anything that isn't a schema (an older server answers 404, and the smoke
 * mock answers `[]`), which sends the form to the static fallback.
 */
export function normaliseSchema(raw: unknown): SearchSchema | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<SearchSchema>;
  if (!Array.isArray(s.platforms) || !Array.isArray(s.filters) || s.platforms.length === 0) return null;
  const filters = s.filters
    .filter((f): f is SearchFilterDef => !!f && typeof f.key === 'string' && typeof f.type === 'string')
    .filter((f) => !QUERY_PARAM_KEYS.includes(f.key) && !STRUCTURAL_KEYS.includes(f.key))
    .map((f) => ({
      ...f,
      dependsOn: f.dependsOn !== undefined ? f.dependsOn : BUILTIN_DEPENDS[f.key] ?? null,
      hiddenWhen: f.hiddenWhen !== undefined ? f.hiddenWhen : BUILTIN_HIDDEN_WHEN[f.key] ?? null,
      list: f.list ?? BUILTIN_LIST.has(f.key),
    }));
  const groupIds = new Set((s.groups ?? []).map((g) => g.id));
  const groups = [...(s.groups ?? [])];
  // A filter naming a group the server didn't list still gets rendered.
  for (const f of filters) {
    if (!groupIds.has(f.group)) {
      groupIds.add(f.group);
      groups.push({ id: f.group, label: f.group, defaultOpen: false });
    }
  }
  return {
    platforms: s.platforms.map((p) => ({ ...p, modes: Array.isArray(p.modes) ? p.modes : [] })),
    filters,
    groups,
    defaults: s.defaults && typeof s.defaults === 'object' ? s.defaults : {},
  };
}

export function filterMap(schema: SearchSchema): Map<string, SearchFilterDef> {
  return new Map(schema.filters.map((f) => [f.key, f]));
}

export function modesFor(schema: SearchSchema, platform: Platform): SearchModeDef[] {
  return schema.platforms.find((p) => p.id === platform)?.modes ?? [];
}

// ── Values ────────────────────────────────────────────────────────────────

const NUMERIC = new Set(['integer', 'number', 'percent']);
const ARRAYS = new Set(['multiselect', 'countryList', 'languageList']);

/** The "nothing chosen" value for a filter. */
export function emptyValue(def: SearchFilterDef): SearchParamValue {
  if (NUMERIC.has(def.type)) return null;
  if (def.type === 'boolean') return false;
  if (ARRAYS.has(def.type)) return [];
  if (def.type === 'select') {
    const neutral = def.options?.find((o) => o.value === '' || o.value === 'any');
    return neutral?.value ?? def.options?.[0]?.value ?? null;
  }
  return '';
}

/** Whether a value does anything (generic, for dependency checks). */
export function truthy(v: SearchParamValue | undefined): boolean {
  if (v === null || v === undefined || v === false) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'string') return v.trim() !== '' && v !== 'any';
  return true;
}

/** Whether a filter's value is set (would be sent). Booleans: only `true`. */
export function isSet(def: SearchFilterDef, v: SearchParamValue | undefined): boolean {
  if (v === undefined || v === null) return false;
  if (NUMERIC.has(def.type)) return typeof v === 'number' ? Number.isFinite(v) : String(v).trim() !== '' && Number.isFinite(Number(v));
  if (def.type === 'boolean') return v === true;
  if (def.type === 'select') return v !== '' && v !== 'any';
  if (Array.isArray(v)) return v.length > 0;
  return String(v).trim() !== '';
}

export function equalValues(a: SearchParamValue | undefined, b: SearchParamValue | undefined): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    const x = Array.isArray(a) ? a : [];
    const y = Array.isArray(b) ? b : [];
    return x.length === y.length && x.every((v, i) => v === y[i]);
  }
  if ((a === null || a === undefined || a === '') && (b === null || b === undefined || b === '')) return true;
  // Select options can arrive as 0 or "0".
  return String(a) === String(b);
}

/** Percent filters: is the wire value a 0–1 fraction (minAudienceShare) or already a percent? */
export function isFractionPercent(def: { max?: number | null }, sample?: unknown): boolean {
  if (def.max != null) return def.max <= 1;
  return typeof sample === 'number' ? sample <= 1 : true;
}

/** Default form params: `schema.defaults` (from Settings), else each filter's empty value. */
export function defaultParams(schema: SearchSchema): Record<string, SearchParamValue> {
  const out: Record<string, SearchParamValue> = {};
  for (const def of schema.filters) {
    const d = schema.defaults[def.key];
    out[def.key] = d !== undefined ? fromWire(def, d) : emptyValue(def);
  }
  return out;
}

export function initialFormState(schema: SearchSchema, platform?: Platform): SearchFormState {
  const available = schema.platforms.filter((p) => p.available);
  const p = platform ?? available[0]?.id ?? schema.platforms[0]?.id ?? 'tiktok';
  return {
    platform: p,
    mode: modesFor(schema, p)[0]?.id ?? 'keyword',
    query: '',
    sounds: [],
    params: defaultParams(schema),
  };
}

/** Form value → what goes in the request. `undefined` = leave it out. */
export function toWire(def: SearchFilterDef, v: SearchParamValue | undefined): unknown {
  if (def.type === 'boolean') return v === true;
  if (!isSet(def, v)) return undefined;
  if (NUMERIC.has(def.type)) return Number(v);
  if (def.type === 'select') {
    const opt = def.options?.find((o) => String(o.value) === String(v));
    return opt ? opt.value : v;
  }
  if (ARRAYS.has(def.type)) return v;
  const text = String(v).trim();
  return def.list ? splitList(text) : text;
}

/** A value from a saved search / history / schema defaults → form value. */
export function fromWire(def: SearchFilterDef, raw: unknown): SearchParamValue {
  if (raw === undefined || raw === null) return emptyValue(def);
  if (NUMERIC.has(def.type)) {
    const n = Number(raw);
    return raw === '' || !Number.isFinite(n) ? null : n;
  }
  if (def.type === 'boolean') return raw === true || raw === 'true';
  if (def.type === 'select') {
    const opt = def.options?.find((o) => String(o.value) === String(raw));
    return opt ? opt.value : emptyValue(def);
  }
  if (ARRAYS.has(def.type)) return Array.isArray(raw) ? raw.map(String) : [String(raw)];
  if (Array.isArray(raw)) return raw.map(String).join(', ');
  return String(raw);
}

// ── Visibility ────────────────────────────────────────────────────────────

export function isApplicable(def: SearchFilterDef, platform: Platform, mode: string): boolean {
  if (def.platforms && def.platforms.length > 0 && !def.platforms.includes(platform)) return false;
  if (def.modes && def.modes.length > 0 && !def.modes.includes(mode)) return false;
  return true;
}

/** Applicable to the platform/mode, its parent is on, and nothing replaces it. */
export function isVisible(def: SearchFilterDef, state: SearchFormState): boolean {
  if (!isApplicable(def, state.platform, state.mode)) return false;
  if (def.dependsOn && !truthy(state.params[def.dependsOn])) return false;
  if (def.hiddenWhen && truthy(state.params[def.hiddenWhen])) return false;
  return true;
}

// ── Query ─────────────────────────────────────────────────────────────────

/** Comma- or newline-separated → trimmed, non-empty entries. */
export function splitList(s: string): string[] {
  return s.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean);
}

/** Keyword lines: newline only, so "tech, news" stays one search. */
export function keywordLines(s: string): string[] {
  return s.split('\n').map((x) => x.trim()).filter(Boolean);
}

/** Handles or profile links → bare, de-duplicated handles. */
export function handleLines(s: string): string[] {
  return [
    ...new Set(
      splitList(s)
        .map((x) => x.replace(/^.*\.com\/@?/, '').replace(/[/?].*$/, '').replace(/^@/, ''))
        .filter(Boolean),
    ),
  ];
}

export function queryEntries(state: Pick<SearchFormState, 'platform' | 'mode' | 'query' | 'sounds'>): string[] {
  if (state.mode === 'sound' && state.platform === 'tiktok') return state.sounds.map((s) => s.musicId);
  if (isKeywordLike(state.mode)) return keywordLines(state.query);
  if (state.mode === 'similar') return handleLines(state.query);
  if (state.mode === 'hashtag') return splitList(state.query).map((h) => h.replace(/^#/, ''));
  return splitList(state.query);
}

export function hasQuery(state: SearchFormState): boolean {
  return queryEntries(state).length > 0;
}

function queryToParams(state: SearchFormState): Partial<ScrapeParams> {
  const entries = queryEntries(state);
  if (state.mode === 'sound' && state.platform === 'tiktok') {
    // Resolved ids only; musicId stays alongside for older servers.
    return { musicIds: entries, musicId: entries[0] ?? '' };
  }
  if (state.mode === 'sound') return { musicIds: entries };
  if (state.mode === 'hashtag') return { hashtags: entries };
  if (state.mode === 'similar') return { seedUsernames: entries };
  if (state.mode === 'usernames') return { usernames: entries };
  return { keywords: entries[0] ?? '', keywordsList: entries.length > 1 ? entries : undefined };
}

// ── Form ↔ request ────────────────────────────────────────────────────────

/** The request body for POST /api/scrape (and what a saved search stores). */
export function toScrapeParams(
  schema: SearchSchema,
  state: SearchFormState,
  folderId: string,
  opts: { presetId?: string | null } = {},
): ScrapeParams {
  const out: Record<string, unknown> = {
    platform: state.platform,
    searchMode: state.mode,
    folderId,
    ...queryToParams(state),
  };
  for (const def of schema.filters) {
    if (!isVisible(def, state)) continue;
    const wire = toWire(def, state.params[def.key]);
    if (wire !== undefined) out[def.key] = wire;
  }
  // An older server knows only the face flag, so it's sent alongside.
  if (out.presenterFilter === 'faces') out.faceCreatorsOnly = true;
  // Sound / tag / similar searches carry no keywords; without this the worker
  // builds no topic terms and a topic minimum passes everyone.
  if (!isKeywordLike(state.mode) && Array.isArray(out.topicTerms) && out.topicTerms.length > 0 && !out.keywordsList) {
    out.keywordsList = out.topicTerms;
  }
  if (opts.presetId) out.presetId = opts.presetId;
  for (const k of Object.keys(out)) if (out[k] === undefined) delete out[k];
  return out as unknown as ScrapeParams;
}

/** A previous search (history, saved search, schedule) → form state. */
export function fromScrapeParams(schema: SearchSchema, raw: Record<string, unknown>): SearchFormState {
  const platform = toPlatform(raw.platform);
  const modes = modesFor(schema, platform);
  const rawMode = String(raw.searchMode ?? 'keyword');
  const mode = modes.some((m) => m.id === rawMode) ? rawMode : (modes.find((m) => m.id === 'keyword') ?? modes[0])?.id ?? 'keyword';
  const arr = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);

  let query = '';
  let sounds: ResolvedSound[] = [];
  if (mode === 'sound' && platform === 'tiktok') {
    const ids = arr(raw.musicIds).length ? arr(raw.musicIds) : raw.musicId ? [String(raw.musicId)] : [];
    // Names aren't stored with a search, so a restored chip shows its id.
    sounds = ids.map((musicId) => ({ musicId, title: null, author: null, coverUrl: null, duration: null }));
  } else if (mode === 'sound') query = arr(raw.musicIds).join('\n');
  else if (mode === 'hashtag') query = arr(raw.hashtags).join('\n');
  else if (mode === 'similar') query = arr(raw.seedUsernames).join('\n');
  else if (mode === 'usernames') query = arr(raw.usernames).join('\n');
  else query = arr(raw.keywordsList).length ? arr(raw.keywordsList).join('\n') : String(raw.keywords ?? '');

  const legacy: Record<string, unknown> = { ...raw };
  if (legacy.presenterFilter === undefined && legacy.faceCreatorsOnly) legacy.presenterFilter = 'faces';
  // In non-word modes keywordsList carried the topic terms.
  if (legacy.topicTerms === undefined && !isKeywordLike(mode) && arr(raw.keywordsList).length) {
    legacy.topicTerms = arr(raw.keywordsList);
  }

  const defaults = defaultParams(schema);
  const params: Record<string, SearchParamValue> = {};
  for (const def of schema.filters) {
    const v = legacy[def.key];
    if (v === undefined || v === null) {
      params[def.key] =
        def.type === 'select' || def.type === 'percent' || ABSENT_MEANS_DEFAULT.has(def.key)
          ? defaults[def.key] ?? emptyValue(def)
          : emptyValue(def);
    } else {
      params[def.key] = fromWire(def, v);
    }
  }
  return { platform, mode, query, sounds, params };
}

/** Switch platform: keep the query and filters, move to that platform's first (strongest) mode. */
export function withPlatform(schema: SearchSchema, state: SearchFormState, platform: Platform): SearchFormState {
  if (state.platform === platform) return state;
  const params = { ...state.params };
  // X is mostly for theme / news pages, so it starts on pages-only; leaving X
  // resets a filter that was never chosen.
  if ('pageFilter' in params) {
    if (platform === 'x' && (params.pageFilter === 'any' || params.pageFilter === '')) params.pageFilter = 'pages';
    else if (state.platform === 'x' && platform !== 'x' && params.pageFilter === 'pages') params.pageFilter = 'any';
  }
  return { ...state, platform, mode: modesFor(schema, platform)[0]?.id ?? state.mode, params };
}

// ── Validation & errors ───────────────────────────────────────────────────

function bound(def: SearchFilterDef, n: number): string | null {
  if (def.min != null && n < def.min) return `Must be at least ${formatBound(def, def.min)}`;
  if (def.max != null && n > def.max) return `Must be at most ${formatBound(def, def.max)}`;
  return null;
}

function formatBound(def: SearchFilterDef, n: number): string {
  if (def.type === 'percent' && isFractionPercent(def, n)) return `${Math.round(n * 100)}%`;
  return n.toLocaleString();
}

/**
 * Client-side checks before a search is spent. The server validates the same
 * body (400 fieldErrors); these just catch the obvious ones first.
 * Keys: filter keys, plus `query` for the query box.
 */
export function validateForm(schema: SearchSchema, state: SearchFormState): FieldErrors {
  const errors: FieldErrors = {};
  const defs = filterMap(schema);
  if (!hasQuery(state)) {
    const label = modesFor(schema, state.platform).find((m) => m.id === state.mode)?.queryLabel ?? 'search term';
    errors.query = `Add at least one ${label.toLowerCase().replace(/s$/, '')}.`;
  }
  for (const def of schema.filters) {
    if (!isVisible(def, state) || !NUMERIC.has(def.type)) continue;
    const v = state.params[def.key];
    if (!isSet(def, v)) continue;
    const msg = bound(def, Number(v));
    if (msg) errors[def.key] = msg;
  }
  // min > max pairs (minFollowers / maxFollowers, …)
  for (const def of schema.filters) {
    if (!def.key.startsWith('min')) continue;
    const maxDef = defs.get(`max${def.key.slice(3)}`);
    if (!maxDef || !isVisible(def, state) || !isVisible(maxDef, state)) continue;
    const lo = state.params[def.key];
    const hi = state.params[maxDef.key];
    if (isSet(def, lo) && isSet(maxDef, hi) && Number(lo) > Number(hi) && !errors[maxDef.key]) {
      errors[maxDef.key] = 'Must be more than the minimum';
    }
  }
  // A topic minimum with nothing to match is a silent no-op in non-word modes.
  const topicMin = defs.get('minTopicMatch');
  const topicTerms = defs.get('topicTerms');
  if (
    topicMin && topicTerms && !isKeywordLike(state.mode) &&
    isVisible(topicMin, state) && isSet(topicMin, state.params.minTopicMatch) && !isSet(topicTerms, state.params.topicTerms)
  ) {
    errors.topicTerms = 'Add at least one topic term, or clear the minimum topic match. With neither, every creator found is kept.';
  }
  // Switched on with no countries would silently run unfiltered.
  const countries = defs.get('audienceCountries');
  if (countries && truthy(state.params.audienceFilter) && isVisible(countries, state) && !isSet(countries, state.params.audienceCountries)) {
    errors.audienceCountries = 'Pick at least one country or group, or switch this off.';
  }
  return errors;
}

export interface MappedFieldErrors {
  /** By filter key. */
  filters: Record<string, string>;
  /** For the query box. */
  query: string | null;
  /** Errors on keys the form doesn't show, as "key: message". */
  other: string[];
}

/** Server 400 `fieldErrors` (keys may be paths like `audienceCountries.0`) → where the form shows them. */
export function mapFieldErrors(fieldErrors: FieldErrors | null | undefined, schema: SearchSchema): MappedFieldErrors {
  const out: MappedFieldErrors = { filters: {}, query: null, other: [] };
  if (!fieldErrors) return out;
  const keys = new Set(schema.filters.map((f) => f.key));
  for (const [path, msg] of Object.entries(fieldErrors)) {
    const base = path.split(/[.[]/)[0] ?? path;
    if (base === 'query' || QUERY_PARAM_KEYS.includes(base)) out.query ??= msg;
    else if (keys.has(base)) out.filters[base] ??= msg;
    else out.other.push(base ? `${base}: ${msg}` : msg);
  }
  return out;
}

/** Group ids holding an error — held open so the message can't hide in a collapsed group. */
export function groupsWithErrors(schema: SearchSchema, errors: Record<string, string>): Set<string> {
  const ids = new Set<string>();
  for (const def of schema.filters) if (errors[def.key]) ids.add(def.group);
  return ids;
}

// ── Summaries ─────────────────────────────────────────────────────────────

export function compactNumber(n: number): string {
  const abs = Math.abs(n);
  const fmt = (x: number) => (Number.isInteger(x) ? String(x) : x.toFixed(1).replace(/\.0$/, ''));
  if (abs >= 1_000_000) return `${fmt(n / 1_000_000)}M`;
  if (abs >= 1_000) return `${fmt(n / 1_000)}K`;
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    return null;
  }
})();
const languageNames = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' });
  } catch {
    return null;
  }
})();

export function placeName(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return code.toUpperCase();
  try {
    return regionNames?.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

function names(codes: string[], fmt: (c: string) => string): string {
  const shown = codes.slice(0, 2).map(fmt).join(', ');
  return codes.length > 2 ? `${shown} +${codes.length - 2}` : shown;
}

/** A filter value as a short readable string ("10K", "30%", "UAE, Saudi Arabia +2"). */
export function formatFilterValue(def: SearchFilterDef, v: SearchParamValue | undefined): string {
  if (v === null || v === undefined) return '—';
  if (def.type === 'boolean') return v === true ? 'On' : 'Off';
  if (def.type === 'percent') {
    const n = Number(v);
    return `${Math.round(isFractionPercent(def, n) ? n * 100 : n)}%`;
  }
  if (def.type === 'integer' || def.type === 'number') {
    const n = Number(v);
    return def.unit === '%' ? `${n}%` : compactNumber(n);
  }
  if (def.type === 'select') return def.options?.find((o) => String(o.value) === String(v))?.label ?? String(v);
  if (def.type === 'multiselect' && Array.isArray(v)) {
    return v.map((x) => def.options?.find((o) => String(o.value) === x)?.label ?? x).join(', ');
  }
  if (def.type === 'countryList' && Array.isArray(v)) return names(v, placeName);
  if (def.type === 'languageList' && Array.isArray(v)) return names(v, (l) => languageNames?.of(l) ?? l);
  return Array.isArray(v) ? v.join(', ') : String(v);
}

export interface ActiveFilterInfo {
  key: string;
  group: string;
  /** "Min followers: 10K", or just the label for an on/off filter. */
  text: string;
}

/**
 * Filters doing something: visible, set, and not just sitting at a non-empty
 * default (Target 100, Sort by relevance). On/off filters count whenever on.
 */
export function activeFilters(schema: SearchSchema, state: SearchFormState): ActiveFilterInfo[] {
  const defaults = defaultParams(schema);
  const out: ActiveFilterInfo[] = [];
  for (const def of schema.filters) {
    if (!isVisible(def, state)) continue;
    const v = state.params[def.key];
    if (!isSet(def, v)) continue;
    if (def.type !== 'boolean' && equalValues(v, defaults[def.key])) continue;
    out.push({
      key: def.key,
      group: def.group,
      text: def.type === 'boolean' ? def.label : `${def.label}: ${formatFilterValue(def, v)}`,
    });
  }
  return out;
}

/** "TikTok · love island edit, reality tv clips" — the collapsed header line. */
export function querySummary(schema: SearchSchema, state: SearchFormState): string {
  const platform = schema.platforms.find((p) => p.id === state.platform)?.label ?? state.platform;
  const entries = queryEntries(state);
  let what: string;
  if (state.mode === 'sound' && state.platform === 'tiktok') {
    what = state.sounds.length === 0 ? 'No sounds yet' : state.sounds.length === 1 ? state.sounds[0]!.title ?? state.sounds[0]!.musicId : `${state.sounds.length} sounds`;
  } else if (state.mode === 'sound') what = `${entries.length} audio`;
  else if (state.mode === 'hashtag') what = entries.map((h) => `#${h}`).join(', ') || 'No hashtags yet';
  else if (state.mode === 'similar') what = `like ${entries.slice(0, 3).map((h) => `@${h}`).join(', ') || '…'}`;
  else what = entries.join(', ') || 'No keywords yet';
  return `${platform} · ${what}`;
}

/**
 * A readable name for a schedule or saved search, taken from what the search
 * is — "#lipsync, #dancetrend" says more later than anything typed in a hurry.
 */
export function searchName(state: SearchFormState): string {
  const entries = queryEntries(state);
  if (state.mode === 'sound') {
    const n = state.platform === 'tiktok' ? state.sounds.map((s) => s.title ?? s.musicId) : entries;
    return n.length ? `Sounds: ${n.join(', ')}`.slice(0, 120) : 'Sound search';
  }
  if (state.mode === 'hashtag') return entries.map((h) => `#${h}`).join(', ').slice(0, 120) || 'Hashtag search';
  if (state.mode === 'similar') return `Like ${entries.slice(0, 3).map((h) => `@${h}`).join(', ')}`.slice(0, 120);
  return entries.join(', ').slice(0, 120) || 'Search';
}

/** Short label for a past search's params (history rows, saved searches). */
export function describeParams(raw: Record<string, unknown>): string {
  const arr = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);
  if (raw.searchMode === 'sound') {
    const ids = [...new Set([...arr(raw.musicIds), ...(raw.musicId != null && raw.musicId !== '' ? [String(raw.musicId)] : [])])];
    return `${ids.length === 1 ? 'Sound' : 'Sounds'}: ${ids.join(', ')}`;
  }
  if (raw.searchMode === 'similar') return `Like ${arr(raw.seedUsernames).slice(0, 2).map((h) => `@${h}`).join(', ')}`;
  if (raw.searchMode === 'hashtag') return arr(raw.hashtags).map((h) => `#${h}`).join(', ');
  if (arr(raw.keywordsList).length) return arr(raw.keywordsList).join(', ');
  return String(raw.keywords ?? '') || '(no keyword)';
}
